import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useQuery } from '@apollo/client';
import { Spinner } from '@chakra-ui/react';
import { TransactionDocument } from '@common/graphql/generated';
import { useIsGoalToGoalTransaction } from '@common/react/goals';
import { DepositOrWithdrawal } from '@common/react/transactions';
import { Text } from '../common/Typography';
import GoalTransactionStatusHeader from './GoalTransactionStatusHeader';
import GoalTransactionStatusView from './GoalTransactionStatusView';

export type GoalTransactionStatusModalProps = {
    transactionId: string;
    transactionDirection: DepositOrWithdrawal;
};

export const showGoalTransactionStatusModal = (
    props: GoalTransactionStatusModalProps
) => ModalManager.show(GoalTransactionStatusModal, props);

const GoalTransactionStatusModal =
    ModalManager.create<GoalTransactionStatusModalProps>(
        ({ transactionId, transactionDirection }) => {
            useTrackModalShown('Goal Transaction Status');

            const modal = useModal();

            const { data, loading } = useQuery(TransactionDocument, {
                variables: { id: transactionId },
                fetchPolicy: 'cache-and-network',
            });
            const transaction = data?.transaction;
            const isGoalToGoalTransaction =
                useIsGoalToGoalTransaction(transaction);

            let header: JSX.Element;
            let content: JSX.Element;
            if (transaction) {
                header = (
                    <GoalTransactionStatusHeader
                        transaction={transaction}
                        goalDirection={
                            isGoalToGoalTransaction
                                ? 'GOAL_TO_GOAL'
                                : transactionDirection
                        }
                    />
                );
                content = (
                    <GoalTransactionStatusView transaction={transaction} />
                );
            } else if (loading) {
                content = <Spinner />;
            } else {
                content = (
                    <Text>
                        Status currently unavailable. Please try again later.
                    </Text>
                );
            }

            return (
                <Modal
                    modalBodyProps={{ pt: 0 }}
                    renderHeader={() => header}
                    size="md"
                    isOpen={modal.visible}
                    onClose={modal.remove}
                >
                    {content}
                </Modal>
            );
        }
    );

export default GoalTransactionStatusModal;
